import { startOfWeek, addDays, format, isSameDay } from "date-fns";
import { WeekViewDayColumn } from "./WeekViewDayColumn";
import { generateHourMarkers } from "@/lib/calendarPositioning";
import { staffSummaryForDay } from "@/lib/staffConflictSummary";
import { Badge } from "@/components/ui/badge";
import { JobStatus } from "../StatusBadge";
import { type AssignedStaffMember } from "@/lib/calendarSpans";
import type { PublicHoliday } from "@/hooks/usePublicHolidays";
import { getHolidayForDate } from "@/hooks/usePublicHolidays";

interface CalendarEvent {
  id: string;
  jobNumber?: string;
  title: string;
  date: Date;
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  status: JobStatus;
  customer: string;
  address?: string;
  phone?: string;
  assignedTo?: string;
  assignedStaff?: AssignedStaffMember[];
  staffColor?: string;
  conflictColor?: string;
  statusColor?: string;
  jobTypeColor?: string;
  providerColor?: string;
  source?: string;
  onSelect?: () => void;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
  primaryColor?: string;
  workdayStart: string;
  workdayEnd: string;
}

interface WeekViewProps {
  currentDate: Date;
  staff: StaffMember[];
  events: CalendarEvent[];
  holidays?: PublicHoliday[];
  onEventMove?: (update: {
    eventId: string;
    newStart: Date;
    newEnd: Date;
    staffId?: string;
  }) => void;
  onOpenJob?: (jobId: string) => void;
  onCreateJob?: (startTime: Date, staffId: string) => void;
}

export function WeekView({ currentDate, staff, events, holidays = [], onEventMove, onOpenJob, onCreateJob }: WeekViewProps) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const days = [...Array(7)].map((_, i) => addDays(weekStart, i));
  const hourMarkers = generateHourMarkers(0, 24);

  const getEventsForStaffAndDay = (staffId: string, day: Date): CalendarEvent[] => {
    return events.filter((event) => {
      const startDate = event.scheduledStartDate || event.date;
      const endDate = event.scheduledEndDate || event.date;

      if (!startDate || !endDate) return false;

      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      const eventStart = new Date(startDate);
      const eventEnd = new Date(endDate);

      const isOnDay = eventStart <= dayEnd && eventEnd >= dayStart;
      if (!isOnDay) return false;

      const isAssignedToStaff = 
        event.assignedTo === staffId ||
        event.assignedStaff?.some((staff) => staff.id === staffId);

      return isAssignedToStaff;
    });
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-background rounded-lg">
      {/* HEADER: Days of week */}
      <div className="flex sticky top-0 z-20 border-b border-border bg-muted/40 backdrop-blur-sm">
        {/* Time axis header - matches the sticky time column width */}
        <div className="w-14 flex-shrink-0 border-r border-border" />
        
        {/* Day headers grid */}
        <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(7, 1fr)` }}>
          {days.map((day) => {
            const isToday = isSameDay(day, new Date());
            const holiday = getHolidayForDate(holidays, day);
            return (
              <div
                key={day.toISOString()}
                className={`
                  py-3 text-center border-r border-border/50 last:border-r-0
                  ${isToday ? "bg-primary/10 dark:bg-primary/20" : ""}
                `}
                data-testid={`week-header-${format(day, "yyyy-MM-dd")}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  {format(day, "EEE")}
                </div>
                <div className={`
                  text-sm font-semibold mt-0.5
                  ${isToday
                    ? "text-primary"
                    : "text-foreground"
                  }
                `}>
                  {format(day, "d MMM")}
                </div>
                {holiday && (
                  <div
                    className="text-[9px] mt-0.5 px-1 py-0.5 rounded truncate mx-1 font-medium"
                    style={{ background: "#FEE2E2", color: "#991B1B" }}
                    title={holiday.localName || holiday.name}
                  >
                    🏖️ {holiday.localName || holiday.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* BODY: Staff lanes with scrollable content */}
      <div className="flex-1 overflow-auto relative">
        {staff.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground">
            <p className="text-sm">No team members to display</p>
          </div>
        ) : (
          <div className="flex">
            {/* Time axis column - sticky left, spans all staff lanes */}
            <div className="w-14 flex-shrink-0 sticky left-0 z-10 bg-muted/50 border-r border-border">
              {staff.map((member, memberIndex) => (
                <div key={member.id}>
                  {/* Staff header spacer */}
                  <div className="h-[36px] border-b border-border/50" />
                  {/* Hour markers for this staff lane */}
                  <div 
                    className="relative border-b border-border" 
                    style={{ height: `${24 * 60}px` }}
                  >
                    {hourMarkers.map((marker) => (
                      <div
                        key={`${member.id}-${marker.hour}`}
                        className="absolute left-0 right-0 flex items-center justify-end pr-2 text-[10px] text-muted-foreground font-medium"
                        style={{ top: `${marker.position}px`, transform: 'translateY(-50%)' }}
                      >
                        {marker.label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Main grid */}
            <div className="flex-1">
              {staff.map((member, memberIndex) => (
                <div
                  key={member.id}
                  className={`
                    border-b border-border last:border-b-0
                    ${memberIndex % 2 === 0 ? "bg-background" : "bg-muted/10"}
                  `}
                  data-testid={`staff-lane-${member.id}`}
                >
                  {/* Staff header row */}
                  <div 
                    className="grid border-b border-border/50 bg-muted/30"
                    style={{ gridTemplateColumns: `repeat(7, 1fr)` }}
                  >
                    <div className="col-span-7 flex items-center gap-2 px-3 py-2">
                      <div 
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: member.primaryColor || '#6366f1' }}
                      />
                      <span className="font-medium text-sm text-foreground">
                        {member.firstName} {member.lastName}
                      </span>
                      {/* Conflict Summary Badge */}
                      {(() => {
                        const staffEvents = events.filter(event => 
                          event.assignedTo === member.id ||
                          event.assignedStaff?.some(staff => staff.id === member.id)
                        ).map(event => ({
                          ...event,
                          startTime: event.scheduledStartDate || event.date,
                          endTime: event.scheduledEndDate || event.date,
                        }));
                        
                        const summary = staffSummaryForDay(member.id, staffEvents, currentDate);

                        if (summary.criticalConflicts > 0) {
                          return (
                            <Badge variant="destructive" className="text-[10px] h-5">
                              {summary.criticalConflicts} Critical
                            </Badge>
                          );
                        }
                        if (summary.highConflicts > 0) {
                          return (
                            <Badge variant="outline" className="text-[10px] h-5 border-amber-400 text-amber-600 dark:text-amber-400">
                              {summary.highConflicts} Conflict{summary.highConflicts > 1 ? 's' : ''}
                            </Badge>
                          );
                        }
                        return (
                          <Badge variant="outline" className="text-[10px] h-5 border-emerald-400 text-emerald-600 dark:text-emerald-400">
                            Available
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Day columns grid */}
                  <div 
                    className="grid"
                    style={{ gridTemplateColumns: `repeat(7, 1fr)` }}
                  >
                    {days.map((day) => {
                      const dayEvents = getEventsForStaffAndDay(member.id, day);
                      const isToday = isSameDay(day, new Date());

                      return (
                        <div
                          key={`${day.toISOString()}-${member.id}`}
                          className={`
                            border-r border-border/50 last:border-r-0
                            ${isToday ? "bg-primary/5 dark:bg-primary/10" : ""}
                          `}
                        >
                          <WeekViewDayColumn
                            day={day}
                            events={dayEvents}
                            staff={member}
                            onEventMove={onEventMove}
                            onOpenJob={onOpenJob}
                            onCreateJob={onCreateJob}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
